import { useEffect, useCallback } from 'react';
import { useNotifications } from '../contexts/NotificationContext';

export interface DataSyncOptions {
  entityType: 'company' | 'rental' | 'contract' | 'device' | 'sim' | 'customer' | 'site';
  onDataUpdate?: (data: any) => void;
  onEntityCreated?: (entity: any) => void;
  onEntityUpdated?: (entity: any) => void;
  onEntityDeleted?: (entityId: string | number) => void;
  enableNotifications?: boolean;
}

export interface UseDataSyncReturn {
  isConnected: boolean;
  sendDataUpdate: (action: 'create' | 'update' | 'delete', entityId: string | number, data?: any) => void;
}

/**
 * Hook สำหรับจัดการ data synchronization แบบ real-time
 * ใช้สำหรับแจ้งเตือนการเปลี่ยนแปลงข้อมูลและอัพเดท UI อัตโนมัติ
 */
const useDataSync = (options: DataSyncOptions): UseDataSyncReturn => {
  const {
    entityType,
    onDataUpdate,
    onEntityCreated,
    onEntityUpdated,
    onEntityDeleted,
    enableNotifications = true
  } = options;

  const { 
    isConnected, 
    sendMessage, 
    addNotification 
  } = useNotifications();

  // ฟังก์ชันสำหรับจัดการการอัพเดทข้อมูล
  const handleDataUpdate = useCallback((event: CustomEvent) => {
    const { type, entityType: updatedEntityType, action, data, entityId } = event.detail;

    // ตรวจสอบว่าเป็นข้อมูลประเภทที่เราสนใจหรือไม่
    if (updatedEntityType !== entityType) {
      return;
    }

    console.log(`Data sync: ${action} ${entityType}`, data);

    // เรียก callback ตามประเภทการดำเนินการ
    switch (action) {
      case 'create':
        onEntityCreated?.(data);
        if (enableNotifications) {
          addNotification({
            type: 'success',
            title: `เพิ่ม${getEntityLabel(entityType)}ใหม่`,
            message: `มีการเพิ่ม${getEntityLabel(entityType)}ใหม่: ${getEntityName(data)}`,
            category: getNotificationCategory(entityType),
            data: { entityType, action, entityId: data.id }
          });
        }
        break;

      case 'update':
        onEntityUpdated?.(data);
        if (enableNotifications) {
          addNotification({
            type: 'info',
            title: `อัพเดท${getEntityLabel(entityType)}`,
            message: `มีการอัพเดท${getEntityLabel(entityType)}: ${getEntityName(data)}`,
            category: getNotificationCategory(entityType),
            data: { entityType, action, entityId: data.id }
          });
        }
        break;

      case 'delete':
        onEntityDeleted?.(entityId);
        if (enableNotifications) {
          addNotification({
            type: 'warning',
            title: `ลบ${getEntityLabel(entityType)}`,
            message: `มีการลบ${getEntityLabel(entityType)}: ${entityId}`,
            category: getNotificationCategory(entityType),
            data: { entityType, action, entityId }
          });
        }
        break;
    }

    // เรียก callback ทั่วไป
    onDataUpdate?.(event.detail);
  }, [entityType, onDataUpdate, onEntityCreated, onEntityUpdated, onEntityDeleted, enableNotifications, addNotification]);

  // ฟังก์ชันสำหรับส่งการอัพเดทข้อมูลไปยัง WebSocket
  const sendDataUpdate = useCallback((
    action: 'create' | 'update' | 'delete', 
    entityId: string | number, 
    data?: any
  ) => {
    if (!isConnected) {
      console.warn('WebSocket not connected, cannot send data update');
      return;
    }

    sendMessage({
      type: 'data_update',
      payload: {
        entityType,
        action,
        entityId,
        data,
        timestamp: new Date().toISOString(),
        userId: localStorage.getItem('userId') || 'anonymous'
      }
    });
  }, [isConnected, sendMessage, entityType]);

  // ลงทะเบียน event listener สำหรับ data updates
  useEffect(() => {
    window.addEventListener('data_update', handleDataUpdate as EventListener);

    return () => {
      window.removeEventListener('data_update', handleDataUpdate as EventListener);
    };
  }, [handleDataUpdate]);

  return {
    isConnected,
    sendDataUpdate
  };
};

// ฟังก์ชันสำหรับแปลงชนิด entity เป็นข้อความไทย
const getEntityLabel = (entityType: string): string => {
  const labels: Record<string, string> = {
    company: 'บริษัท',
    rental: 'การเช่า',
    contract: 'สัญญา',
    device: 'อุปกรณ์',
    sim: 'ซิม',
    customer: 'ลูกค้า',
    site: 'ไซต์'
  };
  return labels[entityType] || entityType;
};

// ฟังก์ชันสำหรับดึงชื่อ entity จากข้อมูล
const getEntityName = (data: any): string => {
  if (!data) return 'ไม่ระบุ';
  
  // ลำดับความสำคัญของฟิลด์ที่จะใช้เป็นชื่อ
  const nameFields = ['name', 'title', 'code', 'id'];
  
  for (const field of nameFields) {
    if (data[field]) {
      return String(data[field]);
    }
  }
  
  return 'ไม่ระบุ';
};

// ฟังก์ชันสำหรับกำหนดหมวดหมู่การแจ้งเตือน
const getNotificationCategory = (entityType: string): 'system' | 'rental' | 'contract' | 'payment' | 'maintenance' => {
  const categoryMap: Record<string, 'system' | 'rental' | 'contract' | 'payment' | 'maintenance'> = {
    company: 'system',
    site: 'system',
    customer: 'system',
    rental: 'rental',
    contract: 'contract',
    device: 'maintenance',
    sim: 'maintenance'
  };
  
  return categoryMap[entityType] || 'system';
};

export default useDataSync;